import React, { useState, useEffect } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import 'primereact/resources/themes/lara-light-purple/theme.css';
import '../salaries.css';
import {
  PendingPaymentCourse,
  PendingPaymentRate,
} from '../../../../api/types/accountants/salaries-pending-payments/salaries-pending-payments.type';
import { getPaymentTypeDisplayText } from '../../../../api/types/settings/lecturers/settings-lecturers-rate-type';
import globalSalaryStore from '../../../../stores/accountant/global-salary-store';
import { SelectedWorkingHourToPay } from '../salaries-generator-page';
import { observer } from 'mobx-react-lite';
import dateUtils from '../../../../utils/date-format';
import { toast } from 'react-toastify';
import { LecturerRateDisplayType } from '../../../../api/types/lecturers/price-rate/lecturer-rate-display-type';

interface SalariesActivityTypeTableProps {
  pendingPaymentCourse: PendingPaymentCourse;
  branchId: number;
}

interface TableRowData {
  id: number;
  activityName: string;
  paymentType: string;
  hours: number; // Individual working hours duration
  rate: number;
  total: number;
  rateItem: PendingPaymentRate; // Store the original rate item for mapping
  workingHourId: number; // Individual working hour ID
  startTime: Date;
  endTime: Date;
}

export const SalariesActivityTypeTable: React.FC<SalariesActivityTypeTableProps> =
  observer(({ pendingPaymentCourse, branchId }) => {
    const [selectedRows, setSelectedRows] = useState<TableRowData[]>([]);

    const currencyTemplate = (
      rowData: TableRowData,
      field: 'rate' | 'total',
    ): string => {
      const value = rowData[field];
      return `₪${(value || 0).toFixed(2)}`;
    };

    const dateTimeTemplate = (
      rowData: TableRowData,
      field: 'startTime' | 'endTime',
    ): string => {
      const date = new Date(rowData[field]);
      return date.toLocaleString('he-IL', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
    };

    const handleSelectionChange = (e: any) => {
      const selectedTableRows: TableRowData[] = e.value;
      setSelectedRows(selectedTableRows);

      // Get current selections from global store
      const currentSelections = [...globalSalaryStore.selectedWorkingHourToPay];

      // Get all working hour IDs from the current table (to remove previous selections from this table)
      const currentTableWorkingHourIds: number[] = [];
      pendingPaymentCourse.rates.forEach((rate) => {
        rate.workingHours.forEach((workingHour) => {
          currentTableWorkingHourIds.push(workingHour.id);
        });
      });

      // Remove any existing selections from this table
      const selectionsFromOtherTables = currentSelections.filter(
        (selection) =>
          !currentTableWorkingHourIds.includes(selection.workingHourId),
      );

      // Create new selections from current table
      const newSelections: SelectedWorkingHourToPay[] = [];

      selectedTableRows.forEach((row) => {
        newSelections.push({
          branchId: branchId,
          courseId: pendingPaymentCourse.courseId,
          courseString: pendingPaymentCourse.courseName,
          rate:
            row.rate *
            (row.rateItem.displayType === LecturerRateDisplayType.OneHour
              ? 1
              : 4 / 3),
          rateId: row.rateItem.rateId,
          quantity: row.hours, // Individual working hour duration
          workingHourId: row.workingHourId, // Individual working hour ID
        });
      });

      // Combine selections from other tables with new selections from current table
      const combinedSelections = [
        ...selectionsFromOtherTables,
        ...newSelections,
      ];

      // Update global store
      globalSalaryStore.selectedWorkingHourToPay = combinedSelections;
    };

    const calculateDuration = (startTime: Date, endTime: Date): number => {
      const start = new Date(startTime);
      const end = new Date(endTime);
      const diffInMs = Math.abs(end.getTime() - start.getTime());
      return diffInMs / (1000 * 60 * 60); // Convert to hours
    };

    const calculateTotalHours = (
      workingHours: { startTime: Date; endTime: Date }[],
    ): number => {
      return workingHours.reduce((total, hours) => {
        const duration = calculateDuration(hours.startTime, hours.endTime);
        return total + duration;
      }, 0);
    };

    const getTableData = (): TableRowData[] => {
      const rows: TableRowData[] = [];

      pendingPaymentCourse.rates.forEach((rateItem: PendingPaymentRate) => {
        const rate = rateItem.rate || 0; // Default to 0 if rate is undefined

        // Create one row for each working hour
        rateItem.workingHours.forEach((workingHour) => {
          const hours = calculateDuration(
            workingHour.startTime,
            workingHour.endTime,
          );
          const total = hours * rate;

          rows.push({
            id: workingHour.id,
            activityName: rateItem.activityName,
            paymentType: getPaymentTypeDisplayText(rateItem.paymentType),
            hours: Math.round(hours * 100) / 100, // Round to 2 decimal places
            rate: rate,
            total:
              (Math.round(total * 100) / 100) *
              (rateItem.displayType === LecturerRateDisplayType.OneHour
                ? 1
                : 4 / 3), // Round to 2 decimal places
            rateItem: rateItem, // Store the original item for mapping
            workingHourId: workingHour.id,
            startTime: workingHour.startTime,
            endTime: workingHour.endTime,
          });
        });
      });

      return rows;
    };

    // Sync selections with global store on mount
    useEffect(() => {
      // If there are existing selections in the global store for this course, restore them
      const existingSelections =
        globalSalaryStore.selectedWorkingHourToPay.filter(
          (item) => item.courseId === pendingPaymentCourse.courseId,
        );

      if (existingSelections.length > 0) {
        const tableData = getTableData();
        const selectedTableRows = tableData.filter((row) =>
          existingSelections.some(
            (selection) => selection.workingHourId === row.workingHourId,
          ),
        );
        setSelectedRows(selectedTableRows);
      }
    }, [pendingPaymentCourse]);

    return (
      <div className="my-unique-datatable-container">
        <DataTable
          value={getTableData()}
          tableStyle={{ minWidth: '50rem' }}
          stripedRows
          showGridlines
          selection={selectedRows}
          onSelectionChange={handleSelectionChange}
          selectionMode="multiple"
          dataKey="id"
          scrollable
          scrollHeight="400px"
        >
          <Column selectionMode="multiple" headerStyle={{ width: '3rem' }} />
          <Column
            field="activityName"
            header="סוג פעילות"
            sortable
            style={{ textAlign: 'right' }}
          />
          <Column
            field="paymentType"
            header="סוג תשלום"
            sortable
            style={{ textAlign: 'right' }}
            body={(rowData) => {
              return (
                <label>
                  {rowData.rateItem.displayType ===
                  LecturerRateDisplayType.AcademicHour
                    ? 'אקדמי'
                    : 'שעתי'}
                </label>
              );
            }}
          />
          <Column
            field="startTime"
            header="שעת התחלה"
            sortable
            style={{ textAlign: 'right' }}
            body={(rowData: TableRowData) =>
              dateUtils.formatTimeFromDate(rowData.startTime)
            }
          />
          <Column
            field="endTime"
            header="שעת סיום"
            sortable
            style={{ textAlign: 'right' }}
            body={(rowData: TableRowData) =>
              dateUtils.formatTimeFromDate(rowData.endTime)
            }
          />
          <Column
            field="hours"
            header="שעות"
            sortable
            style={{ textAlign: 'right' }}
          />
          <Column
            field="rate"
            header="תעריף"
            sortable
            style={{ textAlign: 'right' }}
            body={(rowData) => currencyTemplate(rowData, 'rate')}
          />
          <Column
            field="total"
            header="סה״כ לתשלום"
            sortable
            style={{ textAlign: 'right' }}
            body={(rowData) => currencyTemplate(rowData, 'total')}
          />
        </DataTable>
      </div>
    );
  });
